import type { GameState, SoulAnchorMode } from './types';
import type { BoardGraph } from '@shared/board/graph';
import { appendEvent } from './event-log';
import { modifyKeys } from './economy';
import { applyDamage } from './damage';
import { mulberry32 } from './dice';
import { addDroppedKeys, resolveDroppedKeyPickupAtNode } from './dropped-keys';
import { tryBlockWithStoneSkin } from './stone-skin-block';

export function getSoulAnchorClaimModes(state: GameState): Map<string, SoulAnchorMode> {
  if (state.soulAnchorClaimModes == null) state.soulAnchorClaimModes = new Map();
  return state.soulAnchorClaimModes;
}

export function getSoulAnchorMode(state: GameState, nodeId: string): SoulAnchorMode {
  return getSoulAnchorClaimModes(state).get(nodeId) ?? 'keys';
}

export function randomSoulAnchorMode(state: GameState): SoulAnchorMode {
  const rng = mulberry32(state.rngState);
  state.rngState = rng.nextState;
  return rng.value < 0.5 ? 'keys' : 'hp';
}

/**
 * Return the playerId who has claimed the given nodeId, or null if unclaimed.
 *
 * @param state  - Current game state.
 * @param nodeId - The soul anchor node ID to check.
 * @returns The owner's playerId, or null if unclaimed.
 */
export function getAnchorOwner(state: GameState, nodeId: string): string | null {
  for (const [playerId, player] of state.players) {
    if (player.soulAnchorClaims.includes(nodeId)) return playerId;
  }
  return null;
}

/**
 * Claim a soul anchor node for a player.
 *
 * Rules:
 * - Adds nodeId to player.soulAnchorClaims.
 * - Finds all directly adjacent nodes of type 'soul_anchor' that are currently
 *   unclaimed and claims them too ("connected anchors claim together").
 * - Emits a SoulAnchorClaimEvent listing nodeId and all connectedNodeIds.
 *
 * @param state    - Mutable game state.
 * @param playerId - The player claiming the anchor.
 * @param nodeId   - The soul anchor node being claimed.
 * @param graph    - Board graph used to find adjacent soul_anchor nodes.
 * @throws Error if playerId is not found in state.players.
 */
export function claimSoulAnchor(
  state: GameState,
  playerId: string,
  nodeId: string,
  graph: BoardGraph,
  mode: SoulAnchorMode = 'keys',
): void {
  const player = state.players.get(playerId);
  if (!player) throw new Error(`Player not found: ${playerId}`);

  const claimModes = getSoulAnchorClaimModes(state);

  // Claim the primary node.
  if (!player.soulAnchorClaims.includes(nodeId)) player.soulAnchorClaims.push(nodeId);
  claimModes.set(nodeId, mode);

  // Find and claim all directly adjacent soul_anchor nodes that are unclaimed.
  const connectedIds: string[] = [];
  const node = graph.getNode(nodeId);
  if (node) {
    for (const conn of node.connections) {
      const target = graph.getNode(conn.targetId);
      if (target && target.type === 'soul_anchor' && getAnchorOwner(state, target.id) === null) {
        if (!player.soulAnchorClaims.includes(target.id)) player.soulAnchorClaims.push(target.id);
        claimModes.set(target.id, mode);
        connectedIds.push(target.id);
      }
    }
  }

  appendEvent(state, {
    type: 'soul_anchor_claim',
    playerId,
    nodeId,
    connectedNodeIds: connectedIds,
    mode,
  });
}

/**
 * Trigger the soul anchor effect when a non-owner lands on a claimed anchor.
 *
 * Effect is determined by the owner's claim mode:
 * - Keys mode: steals up to 6 keys from victim and drops them on the anchor tile.
 * - HP mode: deals 9–12 HP damage to victim via applyDamage (source: 'soul_anchor').
 *
 * Emits a SoulAnchorTriggerEvent when the anchor effect lands. A Stone Skin block emits
 * the shared Stone Skin block events instead; HP-mode blocks also emit a 0-amount
 * soul_anchor_trigger for presentation compatibility.
 * Mutates state.rngState only for HP mode.
 *
 * @param state    - Mutable game state.
 * @param victimId - The player who landed on the anchor.
 * @param ownerId  - The player who owns the anchor.
 * @param nodeId   - The soul anchor node that was triggered.
 */
export function triggerSoulAnchor(
  state: GameState,
  victimId: string,
  ownerId: string,
  nodeId: string,
): { died: boolean; keysDropped: number; blockedByStoneSkin?: boolean } {
  const mode = getSoulAnchorMode(state, nodeId);

  if (mode === 'keys') {
    const amount = 6;
    const victim = state.players.get(victimId);
    if (!victim) throw new Error(`Player not found: ${victimId}`);
    if (tryBlockWithStoneSkin(state, victimId, { actorId: ownerId, sourceId: 'soul_anchor' })) {
      return { died: false, keysDropped: 0, blockedByStoneSkin: true };
    }
    const requestedAmount = Math.min(amount, Math.max(0, victim.keys));
    let actualAmount = 0;
    if (requestedAmount > 0) {
      actualAmount = Math.abs(modifyKeys(state, victimId, -requestedAmount, 'soul_anchor_steal'));
      addDroppedKeys(state, nodeId, actualAmount, victimId);
    }
    appendEvent(state, {
      type: 'soul_anchor_trigger',
      playerId: victimId,
      ownerPlayerId: ownerId,
      nodeId,
      effectType: 'keys',
      amount: actualAmount,
    });
    if (actualAmount > 0) {
      resolveDroppedKeyPickupAtNode(state, nodeId);
    }
    return { died: false, keysDropped: actualAmount };
  } else {
    if (tryBlockWithStoneSkin(state, victimId, { actorId: ownerId, sourceId: 'soul_anchor' })) {
      appendEvent(state, {
        type: 'soul_anchor_trigger',
        playerId: victimId,
        ownerPlayerId: ownerId,
        nodeId,
        effectType: 'hp',
        amount: 0,
      });
      return { died: false, keysDropped: 0, blockedByStoneSkin: true };
    }

    // RNG draw: determine damage in range [9, 12].
    const rng = mulberry32(state.rngState);
    state.rngState = rng.nextState;
    const damage = 9 + Math.floor(rng.value * 4); // 9, 10, 11, or 12

    const result = applyDamage(state, victimId, damage, 'soul_anchor', ownerId);
    appendEvent(state, {
      type: 'soul_anchor_trigger',
      playerId: victimId,
      ownerPlayerId: ownerId,
      nodeId,
      effectType: 'hp',
      amount: result.blockedByStoneSkin ? 0 : damage,
    });
    return result;
  }
}
